import classNames from 'classnames';
import { useState } from 'react';

import { Dialog, type DialogProps } from '@revelio/ui';

import modalStyles from '../../components/Modal/themedModal.module.css';
import type { MultiInstanceMeta } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  type FilterId,
  FilterType,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { isPrimaryFilterId } from '../utils/common';
import { canFilterBeSegmented } from '../utils/filterSegmentation';
import styles from './FilterModal.module.css';
import { FilterFormRender } from './filterForms/FilterFormRender';
import { PrimaryFilterFormRender } from './filterForms/PrimaryFilterFormRender';
import { SegmentedFilterFormRender } from './filterForms/SegmentedFilterFormRender';

interface FilterModalProps
  extends Omit<DialogProps, 'children' | 'open' | 'title'> {
  /** Kept as `isOpen` so the three call sites read unchanged. */
  isOpen: boolean;
  /**
   * Chakra-era alias for `placement`. All current call sites pass it; the new
   * Dialog centres by default, so this only has to keep the two ChartConfig
   * ones (outside this sweep) compiling until they are migrated too.
   */
  isCentered?: boolean;
  title?: string;
  filter: Filter | null;
  selectedMultiMeta?: MultiInstanceMeta;
  selectedFilterId?: FilterId;
  groupKeyOverride?: string;
  disabledLevels?: number[];
  selectionLimit?: number;
  currentTreeSelections?: TreeSelection[];
  showSelectedValues?: boolean;
  allowMultiLevelSelect?: boolean;
  allowedPresetValues?: string[];
}

const isTreeFilter = (filter: Filter | null): filter is TreeFilter => {
  return filter?.type === FilterType.TREE;
};

/**
 * FilterModal
 *
 * The <FilterModal> component is a modal version of the <FilterPopover> component.
 */
export const FilterModal = ({
  isOpen,
  isCentered,
  onClose,
  title,
  filter,
  selectedMultiMeta,
  selectedFilterId,
  groupKeyOverride,
  disabledLevels,
  selectionLimit,
  currentTreeSelections,
  showSelectedValues: showSelectedValuesProp,
  allowMultiLevelSelect,
  allowedPresetValues,
  className,
  ...modalProps
}: FilterModalProps) => {
  const [segmentStep, setSegmentStep] = useState<number>(0);

  const showSelectedValues =
    showSelectedValuesProp &&
    selectionLimit !== undefined &&
    selectionLimit > 1;

  const handleSegmentBack = () => {
    if (segmentStep === 2) {
      setSegmentStep(1);
    } else {
      setSegmentStep(0);
    }
  };

  const showPrimaryForm =
    filter?.isPrimary &&
    isPrimaryFilterId(selectedFilterId) &&
    isTreeFilter(filter);
  const showSegmentedForm =
    !showPrimaryForm && filter && canFilterBeSegmented(filter);
  const showBasicForm = !showPrimaryForm && !showSegmentedForm;

  return (
    <Dialog
      {...modalProps}
      open={isOpen}
      onClose={onClose}
      title={title}
      placement={isCentered ? 'center' : 'top'}
      closeButton={false}
      className={classNames(
        modalStyles.content,
        styles.filterDialog,
        className
      )}
      bodyClassName={styles.filterBody}
    >
      {showPrimaryForm && (
        <PrimaryFilterFormRender
          close={onClose}
          selectedFilterId={selectedFilterId}
          primaryFilter={filter}
          groupKeyOverride={groupKeyOverride}
          disabledLevels={disabledLevels}
          selectionLimit={selectionLimit}
          showSelectedValues={showSelectedValues}
          replaceSingleSelections={selectionLimit === 1}
          currentTreeSelections={currentTreeSelections}
          allowMultiLevelSelect={allowMultiLevelSelect}
        />
      )}
      {showSegmentedForm && (
        <SegmentedFilterFormRender
          filter={filter}
          close={onClose}
          selectedMultiMeta={selectedMultiMeta}
          segmentStep={segmentStep}
          setSegmentStep={setSegmentStep}
          handleSegmentBack={handleSegmentBack}
        />
      )}
      {showBasicForm && (
        <FilterFormRender
          filter={filter}
          close={onClose}
          selectedMultiMeta={selectedMultiMeta}
          groupKeyOverride={groupKeyOverride}
          disabledLevels={disabledLevels}
          selectionLimit={selectionLimit}
          showSelectedValues={showSelectedValues}
          replaceSingleSelections={selectionLimit === 1}
          currentTreeSelections={currentTreeSelections}
          allowMultiLevelSelect={allowMultiLevelSelect}
          allowedPresetValues={allowedPresetValues}
        />
      )}
    </Dialog>
  );
};
